import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { getChatbotPublicId } from '../utils/getChatbotPublicId';

interface EmailRecipient {
  id: string;
  email: string;
  name: string | null;
  instantNotifications: boolean;
  dailyReport: boolean;
  weeklyReport: boolean;
}

interface EmailNotificationsStore {
  recipients: EmailRecipient[];
  loading: boolean;
  error: string | null;
  addRecipient: (recipient: Omit<EmailRecipient, 'id'>, chatbotPublicId?: string) => Promise<void>;
  updateRecipient: (id: string, updates: Partial<EmailRecipient>, chatbotPublicId?: string) => Promise<void>;
  removeRecipient: (id: string, chatbotPublicId?: string) => Promise<void>;
  initializeRecipients: (chatbotPublicId: string) => Promise<void>;
}

export const useEmailNotifications = create<EmailNotificationsStore>((set) => ({
  recipients: [],
  loading: false,
  error: null,

  addRecipient: async (recipient, chatbotPublicIdParam) => {
    try {
      set({ loading: true, error: null });
      
      // Get chatbotPublicId if not provided
      const chatbotPublicId = await getChatbotPublicId(chatbotPublicIdParam);
      if (!chatbotPublicId) {
        throw new Error('No chatbot selected');
      }

      // We still need user_id for analytics/tracking
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Use upsert instead of insert to handle duplicate entries
      const { data, error } = await supabase
        .from('email_notification_recipients')
        .upsert({
          chatbot_public_id: chatbotPublicId,
          user_id: user.id,
          email: recipient.email,
          name: recipient.name,
          instant_notifications: recipient.instantNotifications,
          daily_report: recipient.dailyReport,
          weekly_report: recipient.weeklyReport
        }, {
          onConflict: 'chatbot_public_id,email',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) throw error;

      // Check if we need to add a new recipient or update existing one in state
      set(state => {
        const existingIndex = state.recipients.findIndex(r => 
          r.email === data.email && r.id !== data.id
        );

        if (existingIndex >= 0) {
          // Update existing recipient
          const updatedRecipients = [...state.recipients];
          updatedRecipients[existingIndex] = {
            id: data.id,
            email: data.email,
            name: data.name,
            instantNotifications: data.instant_notifications,
            dailyReport: data.daily_report,
            weeklyReport: data.weekly_report
          };
          return { recipients: updatedRecipients, loading: false };
        } else {
          // Add new recipient
          return {
            recipients: [...state.recipients, {
              id: data.id,
              email: data.email,
              name: data.name,
              instantNotifications: data.instant_notifications,
              dailyReport: data.daily_report,
              weeklyReport: data.weekly_report
            }],
            loading: false
          };
        }
      });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error adding recipient', loading: false });
    }
  },

  updateRecipient: async (id, updates, chatbotPublicIdParam) => {
    try {
      set({ loading: true, error: null });
      
      // Get chatbotPublicId if not provided
      const chatbotPublicId = await getChatbotPublicId(chatbotPublicIdParam);
      if (!chatbotPublicId) {
        throw new Error('No chatbot selected');
      }

      const { error } = await supabase
        .from('email_notification_recipients')
        .update({
          email: updates.email,
          name: updates.name,
          instant_notifications: updates.instantNotifications,
          daily_report: updates.dailyReport,
          weekly_report: updates.weeklyReport
        })
        .eq('id', id)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Update recipient in state
      set(state => ({
        recipients: state.recipients.map(r => 
          r.id === id ? { ...r, ...updates } : r
        ),
        loading: false
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error updating recipient', loading: false });
    }
  },

  removeRecipient: async (id, chatbotPublicIdParam) => {
    try {
      set({ loading: true, error: null });
      
      // Get chatbotPublicId if not provided
      const chatbotPublicId = await getChatbotPublicId(chatbotPublicIdParam);
      if (!chatbotPublicId) {
        throw new Error('No chatbot selected');
      }

      const { error } = await supabase
        .from('email_notification_recipients')
        .delete()
        .eq('id', id)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      set(state => ({
        recipients: state.recipients.filter(r => r.id !== id),
        loading: false
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error removing recipient', loading: false });
    }
  },

  initializeRecipients: async (chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('email_notification_recipients')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('created_at');

      if (error) throw error;

      set({
        recipients: data.map(r => ({
          id: r.id,
          email: r.email,
          name: r.name,
          instantNotifications: r.instant_notifications,
          dailyReport: r.daily_report,
          weeklyReport: r.weekly_report
        })),
        loading: false
      });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error loading recipients', loading: false });
    }
  }
}));